import React from 'react'
import { useParams } from 'react-router'
import { CompletionStatus, SectionName } from '_gql'

import { FormattedMessage } from '@divvy-web/i18n'
import { TOAST_TYPE_DANGER, TOAST_TYPE_SUCCESS, useToast } from '@divvy-web/skylab.toast'

import { useApplicationProgress } from '@sputnik/pages/CreditApplicationPages/Navigation/useApplicationProgress'
import { type AddPersonDrawerOpenable } from '@sputnik/pages/CreditApplicationPages/PeopleInfoForm/AddPersonDrawer'
import LimitedAddPersonDrawer from '@sputnik/pages/CreditApplicationPages/PeopleInfoForm/LimitedAddPersonDrawer'
import { useUpdatePeopleInfoData } from '@sputnik/pages/CreditApplicationPages/PeopleInfoForm/UpdatePeopleInfoData.gql'
import { LimitedAddPersonForm } from '@sputnik/schema/creditApplication/addPerson'
import { useWebTracking } from '@sputnik/tracking/WebTrackingContext'
import { logError, logInfo } from '@sputnik/utils/loggerUtils'

const AddOutsourcedAccountantDrawer = ({ isShowing, onClose }: AddPersonDrawerOpenable) => {
  const { appId } = useParams()
  const showToast = useToast()
  const { track } = useWebTracking()

  const completionStatus = useApplicationProgress({ sectionName: SectionName.PEOPLE_INFO })

  const [updateOutsourcedAccountant] = useUpdatePeopleInfoData({
    onCompleted: (updatedData) => {
      const peopleInfoSectionComplete = updatedData.updateCreditApplication?.savedSectionsInfo?.find(
        (section) =>
          section?.sectionName === SectionName.BUSINESS_INFO &&
          section?.completionStatus === CompletionStatus.COMPLETED,
      )

      if (peopleInfoSectionComplete && completionStatus !== CompletionStatus.COMPLETED) {
        track('se_signup__application_people_info_section_progress__completed', {
          is_complete: true,
        })
      }

      logInfo('updateOutsourcedAccountant', {
        attributes: {
          creditAppId: appId,
          result: `outsourced accountant added successfully`,
        },
        eventName: 'updateOutsourcedAccountant',
      })
      showToast(
        TOAST_TYPE_SUCCESS,
        <FormattedMessage
          defaultMessage='Outsourced accountant added'
          id='sputnik.AddOutsourcedAccountantDrawer__qkUuL0'
        />,
        {
          autoHideDelay: 5000,
        },
      )
      onClose()
    },
    onError: (error) => {
      showToast(
        TOAST_TYPE_DANGER,
        <FormattedMessage
          defaultMessage='Unable to add outsourced accountant'
          id='sputnik.AddOutsourcedAccountantDrawer__YSq8eK'
        />,
        {
          className: 'fs-unmask',
          autoHideDelay: 5000,
        },
      )
      logError(error, {
        attributes: {
          action: 'updateOutsourcedAccountant',
          creditAppId: appId,
          result: error,
          message: 'Error while adding outsourced accountant.',
        },
        eventName: 'updateOutsourcedAccountant',
      })
    },
  })

  const onSubmit = async (formValues: LimitedAddPersonForm) => {
    track('se_signup__add_person_drawer_saved__fired', {
      person_type: 'outsourcedAccountant',
    })

    await updateOutsourcedAccountant({
      variables: {
        creditApplicationId: appId ?? '',
        hasOutsourcedAccountant: true,
        outsourcedAccountant: {
          email: formValues.email || '',
          firstName: formValues.firstName || '',
          lastName: formValues.lastName || '',
        },
      },
    })
  }

  return (
    isShowing && (
      <LimitedAddPersonDrawer
        isShowing={isShowing}
        title={
          <FormattedMessage
            defaultMessage='Add an outsourced accountant'
            id='sputnik.AddOutsourcedAccountantDrawer__ihhTbc'
          />
        }
        onClose={onClose}
        onSubmit={onSubmit}
      />
    )
  )
}

export default AddOutsourcedAccountantDrawer
